import { useId, useState, type ReactNode } from 'react'
import { InlineError } from './InlineError'

// Custom in-app replacements for the native browser confirm()/prompt() dialogs, which we never use.
// Both dim the app and follow the modal rule: dismissible only by an explicit action button
// (Cancel or the ✕), never by clicking the backdrop.

export function ConfirmDialog({ title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', danger, confirmDisabled, onConfirm, onCancel }: {
    title: string
    message: ReactNode
    confirmLabel?: string
    cancelLabel?: string
    danger?: boolean
    /**
     * Holds the confirm button shut while the dialog cannot yet answer for what it is about to do
     * — the message is still loading, or what it loaded rules the action out. Cancel and the ✕ are
     * never disabled: a dialog you cannot get out of is worse than one you cannot confirm.
     */
    confirmDisabled?: boolean
    onConfirm: () => void
    onCancel: () => void
}) {
    return (
        <div className="modal-backdrop">
            <div className="modal" style={{ maxWidth: 440 }}>
                <div className="modal-header">
                    <h2>{title}</h2>
                    <button className="modal-close" onClick={onCancel}>✕</button>
                </div>
                <div className="modal-body">
                    <div style={{ margin: 0, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>{message}</div>
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onCancel}>{cancelLabel}</button>
                    <button className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} autoFocus disabled={confirmDisabled} onClick={onConfirm}>{confirmLabel}</button>
                </div>
            </div>
        </div>
    )
}

export function PromptDialog({
    title, label, message, defaultValue = '', placeholder, maxLength, confirmLabel = 'Save', busy = false, error = null,
    onConfirm, onCancel,
}: {
    title: string
    label?: string
    /** What the answer is for, above the field. */
    message?: ReactNode
    defaultValue?: string
    placeholder?: string
    maxLength?: number
    confirmLabel?: string
    /**
     * The answer is being acted on — a save in flight. The field and the confirm button are held
     * until it settles and the dialog stays open: the caller closes it once it has done the thing,
     * or passes `error`. Cancel and the ✕ stay live, as in ConfirmDialog.
     */
    busy?: boolean
    /** Why the last answer was refused, e.g. a name already taken. */
    error?: string | null
    onConfirm: (value: string) => void
    onCancel: () => void
}) {
    const [value, setValue] = useState(defaultValue)
    const inputId = useId()
    const submit = () => { if (value.trim() && !busy) onConfirm(value.trim()) }
    return (
        <div className="modal-backdrop">
            <div className="modal" style={{ maxWidth: 440 }}>
                <div className="modal-header">
                    <h2>{title}</h2>
                    <button className="modal-close" onClick={onCancel}>✕</button>
                </div>
                <div className="modal-body">
                    {message && (
                        <div style={{ margin: '0 0 var(--space-3)', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>{message}</div>
                    )}
                    <div className="form-field" style={{ margin: 0 }}>
                        {label && <label className="form-label" htmlFor={inputId}>{label}</label>}
                        <input
                            id={inputId}
                            className="form-input"
                            autoFocus
                            value={value}
                            placeholder={placeholder}
                            maxLength={maxLength}
                            disabled={busy}
                            onChange={e => setValue(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') submit() }}
                        />
                    </div>
                    {error && <InlineError message={error} style={{ marginTop: 'var(--space-3)' }} />}
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
                    <button className="btn btn-primary" disabled={!value.trim() || busy} onClick={submit}>{confirmLabel}</button>
                </div>
            </div>
        </div>
    )
}
